/**
 * A UUID mint that cannot throw, for the paths where a failed mint would take
 * something larger down with it.
 *
 * `crypto.randomUUID` exists only in a secure context. Measured in Chromium
 * against a Vite dev server, reading
 * `{ secure: isSecureContext, uuid: typeof crypto.randomUUID }`:
 *
 * ```
 * http://localhost:8793/...  -> { "secure": true,  "uuid": "function" }
 * http://10.2.2.246:8793/... -> { "secure": false, "uuid": "undefined" }
 * ```
 *
 * `localhost` is itself a secure context and the deploy target is HTTPS, so
 * the reachable gap is narrow: opening the dev server from another device
 * over a LAN IP — the ordinary way anyone tests a PWA on a phone. Narrow is
 * not never, and logging is the subsystem least entitled to fail: the app's
 * LoggerProvider mints the session id inside a `useState` initializer, where
 * a throw tears down the whole React tree, and core-fields.ts mints an
 * `eventId` per event.
 *
 * Environment-agnostic like the rest of this package (see core-fields.ts):
 * no `import.meta.env` / `process.env` reads, only feature detection on the
 * globals themselves.
 */

/**
 * Render exactly 16 bytes as a v4 UUID, forcing the version nibble (byte 6)
 * and the variant bits (byte 8) per RFC 4122 §4.4.
 */
export function formatUuidV4(bytes: Uint8Array): string {
  const hex = Array.from(bytes, (byte, index) => {
    const value =
      index === 6
        ? (byte & 0x0f) | 0x40
        : index === 8
          ? (byte & 0x3f) | 0x80
          : byte;
    return value.toString(16).padStart(2, "0");
  }).join("");

  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20, 32),
  ].join("-");
}

/**
 * 16 random bytes, degrading rather than throwing.
 *
 * `crypto.getRandomValues` predates `randomUUID` by a decade and needs no
 * secure context, so it covers the plain-HTTP origin above. `Math.random` is
 * the floor under a host that has no `crypto` at all: not
 * cryptographically random, but neither is anything downstream of this file
 * a secret — these bytes become an anonymous correlation id, never a token.
 */
function randomBytes16(): Uint8Array {
  const bytes = new Uint8Array(16);
  try {
    if (
      typeof crypto !== "undefined" &&
      typeof crypto.getRandomValues === "function"
    ) {
      crypto.getRandomValues(bytes);
      return bytes;
    }
  } catch {
    // A host that exposes the name and rejects the call still gets a mint.
  }
  return Uint8Array.from({ length: 16 }, () => Math.floor(Math.random() * 256));
}

/**
 * A v4 UUID, from the platform where it offers one and from
 * `getRandomValues` (or, last of all, `Math.random`) where it does not. Total
 * by construction: no input, no throw, always a well-formed UUID string.
 */
export function randomUUID(): string {
  try {
    if (
      typeof crypto !== "undefined" &&
      typeof crypto.randomUUID === "function"
    ) {
      return crypto.randomUUID();
    }
  } catch {
    // Same reasoning as randomBytes16's catch: name present, call refused.
  }
  return formatUuidV4(randomBytes16());
}
